import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

/**
 * Tiny in-process toast notification. No external dependency on a toast
 * library — replaces ad-hoc `alert()` / `confirm()` calls with a styled,
 * dismiss-after-N-seconds notification that fits the rest of the design
 * system.
 *
 * <p>Why a custom impl: the alternative (react-hot-toast / sonner) is
 * ~10 kB minified and adds another dep at the bottom of the chain. The
 * surface area we actually need is one queue, three severities, and a
 * portal — ~80 lines.
 *
 * <p>Usage:
 *
 * <pre>{@code
 * const toast = useToast();
 * toast.error("Không lưu được. Hãy thử lại.");
 * toast.success("Đã ký lần khám.");
 * }</pre>
 */

export type ToastSeverity = "info" | "success" | "warn" | "error";

interface Toast {
  id: number;
  severity: ToastSeverity;
  message: string;
}

interface ToastApi {
  info: (msg: string) => void;
  success: (msg: string) => void;
  warn: (msg: string) => void;
  error: (msg: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const COLORS: Record<ToastSeverity, { bg: string; border: string; text: string }> = {
  info: { bg: "#eef4ff", border: "#1f6fd4", text: "#0b3b80" },
  success: { bg: "#e9f8ef", border: "#2f9e44", text: "#0e5a26" },
  warn: { bg: "#fff5e2", border: "#caa400", text: "#6b4f00" },
  error: { bg: "#fdecec", border: "#c53030", text: "#7a1d1d" },
};

const DEFAULT_TTL_MS = 4500;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const push = useCallback(
    (severity: ToastSeverity, message: string) => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, severity, message }]);
      // Auto-dismiss. setTimeout is fine — we don't need pause-on-hover for v1.
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, DEFAULT_TTL_MS);
    },
    []
  );

  const api = useMemo<ToastApi>(
    () => ({
      info: (m) => push("info", m),
      success: (m) => push("success", m),
      warn: (m) => push("warn", m),
      error: (m) => push("error", m),
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        style={{
          position: "fixed",
          right: 16,
          bottom: 16,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          zIndex: 9999,
          pointerEvents: "none",
        }}
      >
        {toasts.map((t) => {
          const c = COLORS[t.severity];
          return (
            <div
              key={t.id}
              role="status"
              style={{
                background: c.bg,
                border: `1px solid ${c.border}`,
                color: c.text,
                borderLeft: `4px solid ${c.border}`,
                padding: "10px 14px",
                borderRadius: 8,
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                fontSize: 14,
                maxWidth: 380,
                pointerEvents: "auto",
              }}
              onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
            >
              {t.message}
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Returns the toast API. Outside a {@link ToastProvider} (i.e., before
 * the provider mounts), the calls degrade to {@code console.warn} so the
 * UI doesn't crash — we'd rather lose a notification than the whole
 * shell.
 */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  // Stable fallback so callers don't have to null-check.
  const fallback = useMemo<ToastApi>(
    () => ({
      info: (m) => console.warn("[toast:info]", m),
      success: (m) => console.warn("[toast:success]", m),
      warn: (m) => console.warn("[toast:warn]", m),
      error: (m) => console.warn("[toast:error]", m),
    }),
    []
  );
  useEffect(() => {
    if (!ctx) {
      console.warn(
        "useToast() called outside <ToastProvider>; messages will only appear in console."
      );
    }
  }, [ctx]);
  return ctx ?? fallback;
}
